import { useMemo, useState } from 'react';
import { resolveApiMediaUrl } from '../../api/client.js';
import { productIsSoldOut } from '../../utils/productStock.js';
import { firstDisplayImageRaw } from '../../utils/productImage.js';
import { ProductLowStockHint } from './ProductLowStockHint.jsx';
import { ProductNewHint } from './ProductNewHint.jsx';

/**
 * @param {Record<string, unknown>[]} products
 * @param {boolean} showSections
 * @returns {{ section: string, items: Record<string, unknown>[] }[]}
 */
function groupProductsBySection(products, showSections) {
  if (!showSections) {
    return [{ section: '', items: products }];
  }
  const map = new Map();
  for (const p of products) {
    const sec = String(p?.secao || 'Geral').trim() || 'Geral';
    if (!map.has(sec)) map.set(sec, []);
    map.get(sec).push(p);
  }
  return [...map.entries()]
    .sort((a, b) => a[0].localeCompare(b[0], 'pt-BR'))
    .map(([section, items]) => ({ section, items }));
}

/**
 * @param {{
 *   products: Record<string, unknown>[],
 *   cart: Record<string, number>,
 *   config: { showSections: boolean, showPhotos: boolean },
 *   sectionImages?: Record<string, string>,
 *   productImageCtx?: import('../../utils/productImage.js').ProductImageContext,
 *   addQty: (id: number, delta: number) => void,
 *   setProductQty: (id: number, qty: number) => void,
 *   formatBrl: (n: number) => string,
 *   pricingHidden: boolean,
 *   catalogOnly: boolean,
 *   onOpenProductMedia: (p: Record<string, unknown>) => void,
 *   onRequireLogin: () => void,
 * }} props
 */
export function StoreCatalogList({
  products,
  cart,
  config,
  sectionImages = {},
  productImageCtx,
  addQty,
  setProductQty,
  formatBrl,
  pricingHidden,
  catalogOnly,
  onOpenProductMedia,
  onRequireLogin,
}) {
  const groups = useMemo(
    () => groupProductsBySection(products, config.showSections),
    [products, config.showSections],
  );

  return (
    <div className="space-y-6">
      {groups.map(({ section, items }) => (
        <section key={section || '__all'} className="space-y-1">
          {section ? (
            <h2 className="sticky top-[var(--store-catalog-sticky-top,7rem)] z-[28] -mx-1 flex items-center gap-2 border-b border-zinc-200 bg-zinc-50 px-2 py-2 text-[11px] font-bold uppercase tracking-wider text-zinc-500 sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0 sm:text-xs dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-400 sm:dark:bg-transparent">
              {sectionImages[section] ? (
                <img
                  src={resolveApiMediaUrl(sectionImages[section])}
                  alt=""
                  className="h-7 w-7 shrink-0 rounded-lg border border-zinc-200/80 object-cover dark:border-zinc-700"
                />
              ) : null}
              <span>{section}</span>
            </h2>
          ) : null}
          <ul className="divide-y divide-zinc-100 overflow-hidden rounded-2xl border border-zinc-100 bg-white shadow-sm dark:divide-zinc-800 dark:border-zinc-800 dark:bg-zinc-900">
            {items.map((p) => (
              <StoreCatalogListRow
                key={String(p.id)}
                product={p}
                qty={Number(cart[p.id] || 0)}
                showPhotos={config.showPhotos}
                productImageCtx={productImageCtx}
                addQty={addQty}
                setProductQty={setProductQty}
                formatBrl={formatBrl}
                pricingHidden={pricingHidden}
                catalogOnly={catalogOnly}
                onOpenProductMedia={onOpenProductMedia}
                onRequireLogin={onRequireLogin}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/**
 * @param {{
 *   product: Record<string, unknown>,
 *   qty: number,
 *   showPhotos: boolean,
 *   productImageCtx?: import('../../utils/productImage.js').ProductImageContext,
 *   addQty: (id: number, delta: number) => void,
 *   setProductQty: (id: number, qty: number) => void,
 *   formatBrl: (n: number) => string,
 *   pricingHidden: boolean,
 *   catalogOnly: boolean,
 *   onOpenProductMedia: (p: Record<string, unknown>) => void,
 *   onRequireLogin: () => void,
 * }} props
 */
function StoreCatalogListRow({
  product: p,
  qty,
  showPhotos,
  productImageCtx,
  addQty,
  setProductQty,
  formatBrl,
  pricingHidden,
  catalogOnly,
  onOpenProductMedia,
  onRequireLogin,
}) {
  const [editingQty, setEditingQty] = useState(false);
  const [draftQty, setDraftQty] = useState('');

  const pid = Number(p.id);
  const soldOut = productIsSoldOut(p);
  const erp = p.codigo_erp != null && String(p.codigo_erp).trim() !== '' ? String(p.codigo_erp).trim() : '';
  const code = erp || String(p.id).padStart(6, '0');
  const imgRaw = firstDisplayImageRaw(p, productImageCtx);
  const imgUrl = imgRaw ? resolveApiMediaUrl(imgRaw) : '';
  const hasThumb = showPhotos && imgUrl !== '';

  function commitDraft() {
    const n = Math.max(0, Math.floor(Number(draftQty.replace(',', '.')) || 0));
    setEditingQty(false);
    setProductQty(pid, n);
  }

  return (
    <li
      id={`store-produto-${pid}`}
      className={`flex items-center gap-2 px-2 py-2.5 sm:gap-3 sm:px-3 sm:py-3 ${qty > 0 ? 'bg-[rgb(var(--st-rgb)/0.06)] dark:bg-[rgb(var(--st-rgb)/0.1)]' : ''}`}
    >
      {showPhotos ? (
        hasThumb ? (
          <button
            type="button"
            aria-label={`Ver fotos: ${String(p.nome || 'produto')}`}
            className="relative h-11 w-11 shrink-0 overflow-hidden rounded-lg border border-zinc-200 bg-zinc-100 transition hover:ring-2 hover:ring-[var(--st-500)] dark:border-zinc-700 dark:bg-zinc-800"
            onClick={() => onOpenProductMedia(p)}
          >
            <img src={imgUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
            <ProductNewHint product={p} overlay className="!text-[8px]" />
            {!soldOut ? <ProductLowStockHint product={p} overlay className="!text-[8px]" /> : null}
          </button>
        ) : (
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-zinc-100 text-sm font-bold uppercase text-[var(--st-600)] dark:bg-zinc-800 dark:text-[var(--st-400)]"
            aria-hidden
          >
            {(String(p.nome || '?').trim().slice(0, 1) || '?')}
          </div>
        )
      ) : null}

      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold leading-snug text-zinc-900 dark:text-zinc-50">{String(p.nome || '')}</p>
        <p className="text-[10px] text-zinc-500 dark:text-zinc-400">Cód. {code}</p>
        {!showPhotos ? (
          <div className="mt-1 flex flex-wrap items-center gap-1">
            <ProductNewHint product={p} overlay={false} />
            {!soldOut ? <ProductLowStockHint product={p} overlay={false} /> : null}
          </div>
        ) : null}
      </div>

      <div className="flex shrink-0 flex-col items-end gap-1.5 sm:flex-row sm:items-center sm:gap-3">
        {!pricingHidden ? (
          <span className="text-sm font-bold tabular-nums text-zinc-900 dark:text-white">{formatBrl(Number(p.preco))}</span>
        ) : null}

        {!catalogOnly ? (
          pricingHidden ? (
            <button
              type="button"
              className="rounded-lg bg-zinc-900 px-3 py-1.5 text-[11px] font-semibold text-white dark:bg-zinc-100 dark:text-zinc-900"
              onClick={onRequireLogin}
            >
              Entrar
            </button>
          ) : soldOut ? (
            <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-400">
              Sem stock
            </span>
          ) : (
            <div className="flex items-center gap-1">
              <button
                type="button"
                aria-label="Remover uma unidade"
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-zinc-200 text-base font-bold text-zinc-800 transition active:scale-95 dark:bg-zinc-700 dark:text-zinc-100"
                onClick={() => addQty(pid, -1)}
              >
                −
              </button>
              {editingQty ? (
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  aria-label="Quantidade"
                  className="h-8 w-12 rounded-lg border border-zinc-300 bg-white text-center text-sm font-bold tabular-nums text-zinc-900 outline-none focus:border-[var(--st-500)] focus:ring-1 focus:ring-[var(--st-500)] dark:border-zinc-600 dark:bg-zinc-800 dark:text-white"
                  value={draftQty}
                  onChange={(e) => setDraftQty(e.target.value.replace(/[^\d]/g, ''))}
                  onBlur={commitDraft}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitDraft();
                    }
                    if (e.key === 'Escape') {
                      setEditingQty(false);
                    }
                  }}
                  autoFocus
                />
              ) : (
                <button
                  type="button"
                  aria-label="Editar quantidade"
                  className="flex h-8 min-w-[2rem] items-center justify-center rounded-lg border border-transparent px-1 text-sm font-bold tabular-nums text-zinc-900 transition hover:border-zinc-300 hover:bg-zinc-50 dark:text-white dark:hover:border-zinc-600 dark:hover:bg-zinc-800"
                  onClick={() => {
                    setDraftQty(String(qty || 0));
                    setEditingQty(true);
                  }}
                >
                  {qty}
                </button>
              )}
              <button
                type="button"
                aria-label="Adicionar uma unidade"
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-[var(--st-500)] to-[var(--st-600)] text-base font-bold text-white transition active:scale-95"
                onClick={() => addQty(pid, 1)}
              >
                +
              </button>
            </div>
          )
        ) : null}
      </div>
    </li>
  );
}
